import React, { useState, useRef, useCallback } from 'react';
import Webcam from 'react-webcam';
import {
  Box,
  Button,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  Backdrop
} from '@mui/material';
import {
  CameraAlt,
  Check,
  Close,
  FlipCameraAndroid
} from '@mui/icons-material';

/**
 * Crop image to center circle (for selfie frame). Returns new data URL.
 */
function cropImageToCircle(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      const size = Math.min(w, h);
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(dataUrl);
        return;
      }
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, size, size);
      ctx.beginPath();
      ctx.arc(size / 2, size / 2, size / 2, 0, 2 * Math.PI);
      ctx.closePath();
      ctx.clip();
      const sx = (w - size) / 2;
      const sy = (h - size) / 2;
      ctx.drawImage(img, sx, sy, size, size, 0, 0, size, size);
      try {
        resolve(canvas.toDataURL('image/jpeg', 0.92));
      } catch {
        resolve(dataUrl);
      }
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

/**
 * DocumentCapture Component
 *
 * Real camera capture with stubbed CV guidance.
 * Shows real-time feedback to guide user.
 * Auto-captures when quality is good.
 *
 * Props:
 * - documentType: 'national_id', 'passport', etc.
 * - onCapture: (imageData, metadata) => void
 * - sessionId: UUID of KYC session
 * - selfieSilhouetteUrl: optional image URL for selfie overlay (head+neck silhouette); when provided, used instead of built-in shape
 */
const DocumentCapture = ({
  documentType = 'national_id',
  onCapture,
  sessionId,
  selfieSilhouetteUrl
}) => {
  const webcamRef = useRef(null);
  const captureTimeoutRef = useRef(null);
  const isMountedRef = useRef(true);
  const [capturing, setCapturing] = useState(false);
  const [captured, setCaptured] = useState(false);
  const [imageData, setImageData] = useState(null);
  const [guidance, setGuidance] = useState('Position document in frame');
  const [quality, setQuality] = useState(0);
  const [facingMode, setFacingMode] = useState('environment'); // back camera
  const [error, setError] = useState(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Clear capture timeout on unmount so we never run handleCapture after unmount
  React.useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (captureTimeoutRef.current) {
        clearTimeout(captureTimeoutRef.current);
        captureTimeoutRef.current = null;
      }
    };
  }, []);

  const handleCapture = useCallback(async () => {
    if (captureTimeoutRef.current) {
      clearTimeout(captureTimeoutRef.current);
      captureTimeoutRef.current = null;
    }
    if (!isMountedRef.current) return;
    if (!webcamRef.current) {
      setError('Camera not ready. Please wait for the camera to start.');
      return;
    }

    let imageSrc = webcamRef.current.getScreenshot();
    if (!imageSrc) {
      setError('Camera stream not ready yet. Please allow camera access, wait a moment, and try again.');
      return;
    }

    // For selfie with circular frame, crop to center circle so only content inside the frame is kept
    if (documentType === 'selfie' && selfieSilhouetteUrl) {
      imageSrc = await cropImageToCircle(imageSrc);
    }

    setImageData(imageSrc);
    setCaptured(true);
    setCapturing(false);

    if (onCapture) {
      const metadata = {
        quality: quality,
        documentDetected: true,
        timestamp: new Date().toISOString(),
        documentType,
        sessionId
      };
      setUploading(true);
      try {
        await onCapture(imageSrc, metadata);
      } finally {
        setUploading(false);
      }
    }
  }, [quality, documentType, sessionId, onCapture, selfieSilhouetteUrl]);

  // Simulated CV guidance (will be replaced with real CV in Phase 8B-Real)
  // Only run when camera stream is ready so getScreenshot() can succeed
  const analyzeFrame = useCallback(() => {
    if (!capturing || captured || !cameraReady) return;

    // Simulate quality increasing as user holds steady
    setQuality(prev => {
      const newQuality = Math.min(prev + 5, 100);

      if (newQuality < 40) {
        setGuidance('Move closer to document');
      } else if (newQuality < 70) {
        setGuidance('Hold steady...');
      } else if (newQuality < 90) {
        setGuidance('Almost there...');
      } else {
        setGuidance('Perfect! Capturing...');
        // Auto-capture only once when we first reach 100%; store timeout so we can clear on unmount
        if (prev < 100) {
          if (captureTimeoutRef.current) clearTimeout(captureTimeoutRef.current);
          captureTimeoutRef.current = setTimeout(() => {
            captureTimeoutRef.current = null;
            handleCapture();
          }, 500);
        }
      }

      return newQuality;
    });
  }, [capturing, captured, cameraReady, handleCapture]);

  // Start analyzing frames only when capturing and camera is ready; clear capture timeout when canceling
  React.useEffect(() => {
    if (!capturing) {
      if (captureTimeoutRef.current) {
        clearTimeout(captureTimeoutRef.current);
        captureTimeoutRef.current = null;
      }
      return;
    }
    if (!cameraReady) return;

    const interval = setInterval(analyzeFrame, 200);
    return () => clearInterval(interval);
  }, [capturing, cameraReady, analyzeFrame]);

  const handleStartCapture = () => {
    setError(null);
    setCameraError(null);
    if (!cameraReady) {
      setError('Please wait for the camera to start, then try again.');
      return;
    }
    setCapturing(true);
    setQuality(0);
    setGuidance('Position document in frame');
  };

  const handleRetake = () => {
    setImageData(null);
    setCaptured(false);
    setQuality(0);
  };

  const toggleCamera = () => {
    setCameraReady(false);
    setFacingMode(prev => prev === 'user' ? 'environment' : 'user');
  };

  const videoConstraints = {
    width: { ideal: 1280 },
    height: { ideal: 720 },
    facingMode: facingMode
  };

  const handleUserMedia = useCallback(() => {
    setCameraReady(true);
    setCameraError(null);
  }, []);

  const handleUserMediaError = useCallback((err) => {
    console.error('Camera error:', err);
    setCameraReady(false);
    setCameraError(err?.message || 'Could not access camera. Please allow camera permission and use HTTPS.');
  }, []);

  const getQualityColor = () => {
    if (quality < 40) return 'error';
    if (quality < 70) return 'warning';
    return 'success';
  };

  return (
    <Paper elevation={3} sx={{ p: 3, maxWidth: 600, mx: 'auto' }}>
      <Typography variant="h5" gutterBottom>
        Capture {documentType.replace('_', ' ').toUpperCase()}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {cameraError && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setCameraError(null)}>
          {cameraError}
        </Alert>
      )}

      {/* Guidance and quality: always above the camera frame */}
      {capturing && (
        <Box sx={{ mb: 2 }}>
          <Alert
            severity={quality > 70 ? 'success' : 'info'}
            icon={quality > 90 ? <Check /> : undefined}
            sx={{ mb: 1 }}
          >
            {guidance}
          </Alert>
          <Stack direction="row" spacing={1} justifyContent="center">
            <Chip
              label={`Quality: ${quality}%`}
              color={getQualityColor()}
              size="small"
            />
            {quality > 90 && (
              <CircularProgress size={20} color="success" />
            )}
          </Stack>
        </Box>
      )}

      {/* Success message above the image so it is never clipped by the frame */}
      {captured && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Document captured successfully!
        </Alert>
      )}

      <Box
        sx={{
          position: 'relative',
          mb: 2,
          minHeight: 320,
          overflow: 'hidden',
          borderRadius: documentType === 'selfie' && selfieSilhouetteUrl ? '50%' : '8px',
          ...(documentType === 'selfie' && selfieSilhouetteUrl && {
            width: '100%',
            maxWidth: 360,
            aspectRatio: '1/1',
            mx: 'auto'
          })
        }}
      >
        {!captured ? (
          <>
            {!cameraReady && !cameraError && (
              <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                <CircularProgress size={40} sx={{ mb: 2 }} />
                <Typography>Starting camera...</Typography>
              </Box>
            )}
            <Webcam
              ref={webcamRef}
              audio={false}
              screenshotFormat="image/jpeg"
              videoConstraints={videoConstraints}
              onUserMedia={handleUserMedia}
              onUserMediaError={handleUserMediaError}
              style={{
                width: '100%',
                height: documentType === 'selfie' && selfieSilhouetteUrl ? '100%' : undefined,
                objectFit: 'cover',
                borderRadius: documentType === 'selfie' && selfieSilhouetteUrl ? '50%' : '8px',
                display: cameraReady ? (capturing || !imageData ? 'block' : 'none') : 'none',
                ...(documentType === 'selfie' && selfieSilhouetteUrl && { position: 'absolute', inset: 0 })
              }}
            />

            {/* Overlay: frame guide or silhouette only (no text) */}
            {capturing && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  pointerEvents: 'none'
                }}
              >
                {documentType === 'selfie' ? (
                  <>
                    {selfieSilhouetteUrl ? (
                      <Box
                        component="img"
                        src={selfieSilhouetteUrl}
                        alt=""
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          objectPosition: 'center center',
                          pointerEvents: 'none'
                        }}
                      />
                    ) : (
                      <>
                        <svg width={0} height={0} aria-hidden="true">
                          <defs>
                            <mask id="selfieHeadNeckMask" maskUnits="objectBoundingBox" maskContentUnits="objectBoundingBox">
                              <rect x={0} y={0} width={1} height={1} fill="white" />
                              <ellipse cx={0.5} cy={0.32} rx={0.24} ry={0.26} fill="black" />
                              <path d="M 0.32 0.56 L 0.68 0.56 L 0.64 1 L 0.36 1 Z" fill="black" />
                            </mask>
                          </defs>
                        </svg>
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            bgcolor: 'rgba(0,0,0,0.7)',
                            mask: 'url(#selfieHeadNeckMask)',
                            maskSize: 'cover',
                            WebkitMaskImage: 'url(#selfieHeadNeckMask)',
                            WebkitMaskSize: 'cover',
                            pointerEvents: 'none'
                          }}
                        />
                        <Box
                          component="svg"
                          viewBox="0 0 100 100"
                          preserveAspectRatio="xMidYMid meet"
                          sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            width: '50%',
                            height: '65%',
                            transform: 'translate(-50%, -50%)',
                            pointerEvents: 'none'
                          }}
                        >
                          <ellipse cx="50" cy="32" rx="24" ry="26" fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth="2" />
                          <path d="M 32 56 L 68 56 L 64 100 L 36 100 Z" fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth="2" />
                        </Box>
                      </>
                    )}
                  </>
                ) : (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      border: '3px dashed',
                      borderColor: quality > 70 ? 'success.main' : 'primary.main',
                      borderRadius: '12px',
                      height: '60%',
                      width: '80%',
                      transition: 'border-color 0.3s'
                    }}
                  />
                )}
              </Box>
            )}
          </>
        ) : (
          <Box sx={{ textAlign: 'center' }}>
            <img
              src={imageData}
              alt="Captured document"
              style={{
                width: '100%',
                maxWidth: documentType === 'selfie' && selfieSilhouetteUrl ? 360 : undefined,
                borderRadius: documentType === 'selfie' && selfieSilhouetteUrl ? '50%' : '8px',
                border: '2px solid #4caf50'
              }}
            />
          </Box>
        )}
      </Box>

      {/* Action Buttons */}
      <Stack direction="row" spacing={2}>
        {!capturing && !captured && (
          <Button
            variant="contained"
            startIcon={<CameraAlt />}
            onClick={handleStartCapture}
            fullWidth
            disabled={!cameraReady}
          >
            {cameraReady ? 'Start Capture' : 'Waiting for camera...'}
          </Button>
        )}

        {capturing && (
          <Button
            variant="outlined"
            startIcon={<Close />}
            onClick={() => setCapturing(false)}
            fullWidth
          >
            Cancel
          </Button>
        )}

        {!captured && (
          <Button
            variant="text"
            startIcon={<FlipCameraAndroid />}
            onClick={toggleCamera}
            size="small"
          >
            Flip
          </Button>
        )}
      </Stack>

      <Backdrop open={uploading} sx={{ position: 'absolute', zIndex: 1 }}>
        <CircularProgress />
      </Backdrop>
    </Paper>
  );
};

export default DocumentCapture;
